import React from 'react';
import {
  Navigation,
  Page,
  Image,
  Storage,
  TaskSorting,
  TaskMultiChoice
} from "@bittnerdevelopmentgroup/interactive";

import verticalGraph from './assets/PRINECO3_FIG26.07-longrun.jpg'
import diagGraph from './assets/PRINECO3_FIG26.09-shortrun.jpg'
import panel2 from './assets/Int_PrinEco5_26-2_Custom.jpg'
import panel4 from './assets/Int_PrinEco5_26-4_Custom.jpg'

const ShortRunAggregateSupply = () => {
  return <>
    <div className="sr-only" aria-hidden="true">
      {[verticalGraph, diagGraph, panel2, panel4].map(src => <img alt="" src={src} />)}
    </div>
    <Navigation interactiveId="chapter-26">
      <Page>
        <p>
          You’ve learned that the long-run aggregate supply (LRAS) curve is vertical, because in the long run, the price level doesn’t
          affect the quantity.
        </p>
        <p align='center' style={{marginBottom: "8px"}}>
          <b style={{fontSize: "16.5px"}}>
            The Long-Run Aggregate Supply Curve
          </b>
        </p>
        <Image
          alt="A graph titled The Long-Run Aggregate Supply Curve."
          longAlt={"A graph titled The Long-Run Aggregate Supply Curve. It has Real G D P on the horizontal axis and Price level on the vertical axis. A vertical line is labeled L R A S and has a real G D P value of Y star, where u equals u star. The output level along the curve remains unchanged as the price level from 90 to 100, to 110."}
          src={verticalGraph}
          style={{ display: "flex", justifyContent: "center", margin: "auto" }}
          width="55%"
        />
        <p>
          The short-run aggregate supply (SRAS) curve, on the other hand, has a positive slope, meaning that increases in the economy’s price
          level increase the quantity of aggregate supply and decreases in the price level will decrease the quantity in the short run.
        </p>
        <p align='center' style={{marginBottom: "8px"}}>
          <b style={{fontSize: "16.5px"}}>
            The Short-Run Aggregate Supply Curve
          </b>
        </p>
        <Image
          alt="A line graph titled The Short-Run Aggregate Supply Curve."
          longAlt={"A line graph titled The Short-Run Aggregate Supply Curve. It has Real G D P on the horizontal axis and Price level on the vertical axis. An upward sloping line is labeled S R A S. When price level is 90, real G D P is 19 trillion dollars; when price level is 100, real G D P is 20 trillion dollars; when price level is 110, real G D P is 21 trillion dollars."}
          src={diagGraph}
          style={{ display: "flex", justifyContent: "center", margin: "auto" }}
          width="55%"
        />
        <p>
          Why would higher price levels cause more supply? Let’s consider that question.
        </p>
      </Page>
      <Page>
        <p>
          Imagine you run a custom T-shirt business on campus. Fellow students order shirts for clubs, events,
          and intramural teams. There are certain inputs you have to pay for: the wages of your workers; the rent
          of your workspace; and suppliers for blank shirts and printing materials.
        </p>
        <p>
          You use all of that to create the <b>output</b>, which is a finished T-shirt.
        </p>
        <p align='center'>
          <Image width="80%" alt="A photo of black or dark navy T shirts having a design silk screened onto them with bright yellow ink." src={panel2} photoCredit={"Shutterstock / KOKTARO"} />
        </p>
      </Page>
      <Page>
        <p>
          <b>
            If the market price for T-shirts increases, which of the following things can change immediately, and which might take a while?
          </b>
        </p>
        <TaskSorting
          state={Storage()}
          feedbackCorrect={<span>Notice that the things that are harder to change the price of are all inputs, and the thing that is easiest to change the price of is an output. Economists would say that input prices tend to be <b>stickier</b>, or harder to change, in the short run.</span>}
          feedbackIncorrect="Hint: there should be only one option that can change immediately. The rest will take a while."
          prompt={<i>
            Sort each of the following options based on which can change immediately, and which might take a while.
          </i>}
          categories={[
            "Can change immediately",
            "Will take a while"
          ]}
          maxOptionsPerRow={2}
        >
          <TaskSorting.Option category={1}>
            The monthly rent on your space. You have a three-year contract on the lease.
          </TaskSorting.Option>
          <TaskSorting.Option category={1}>
            The cost of wages. Each of your employees signed an employment contract at an hourly rate of $17 when they started.
          </TaskSorting.Option>
          <TaskSorting.Option category={1}>
            The cost of blank T-shirts. Your supplier guaranteed the price for one year.
          </TaskSorting.Option>
          <TaskSorting.Option category={0}>
            The price you charge for one custom T-shirt as advertised on your website.
          </TaskSorting.Option>
        </TaskSorting>
      </Page>
      <Page>
        <p>
          Now, imagine that one detail has changed. Instead of just listing prices on your website, they’re also printed on a large shop banner.
        </p>
        <p align='center'>
          <Image width="80%" alt="A photo of a customer pointing to a menu that offers solid color T shirts in dozens of different hues." src={panel4} photoCredit={"Shutterstock / tativophotos"} />
        </p>
        <p>
          To increase your prices, you’ll have to reconfigure the banner and have it remade. So, while other
          custom T-shirt shops on campus are revising their websites and jacking up prices, your prices remain
          low in the short run. However, since your T-shirts are now relatively cheaper than the rest of the
          market, customers are demanding more T-shirts at your shop’s low, low prices.
        </p>
        <TaskMultiChoice
          state={Storage()}
          prompt={<b>How do you respond to this increased demand?</b>}
          choices={[
            "I do nothing. So what if we run out of T-shirts by noon?",
            "I shorten my hours. We don’t make enough T-shirts to sell for the whole day.",
            "I start making more T-shirts! I might not be able to increase my prices right now, but I can increase supply to capture some of that demand."]
          }
          correct={[2]}
          feedback={[
            "Remember, as the shop owner, your job is to keep profit as high as possible.",
            "Remember, as the shop owner, your job is to keep profit as high as possible.",
            <span>As you’ve learned, these are quite literally called menu costs: the costs of changing prices. Even if you can’t increase prices immediately, increasing your supply to meet demand for your comparatively cheap product still causes an increase in supply in the short run.</span>,
          ]}
          randomize={false}
        />
      </Page>
      <Page>
        <p>
          Now something unexpected happens: there’s a natural disaster in China, which is the world’s primary
          producer of and exporter of apparel. Your T-shirt supplier is unable to fill your orders, and the same
          thing is happening to shops like yours (and firms in other industries) everywhere.
        </p>
        <p align='center' style={{marginBottom: "8px"}}>
          <b style={{fontSize: "16.5px"}}>
            The Short-Run Aggregate Supply Curve
          </b>
        </p>
        <Image
          alt="A line graph has Real GDP, in trillion of dollars, on the x axis and Price level (P) on the y axis. A positive, diagonal line is labeled SRAS. When price level is 90, real GDP is $19 trillion; when price level is 100, real GDP is $20 trillion; when price level is 110, real GDP is $21 trillion."
          src={diagGraph}
          width="55%"
          style={{ display: "flex", justifyContent: "center", margin: "auto" }}
        />
        <TaskMultiChoice
          state={Storage()}
          prompt={<b>What happens to the aggregate supply curve?</b>}
          choices={[
            "stays the same",
            "shifts to the right",
            "shifts to the left"
          ]}
          correct={[2]}
          feedback={[
            "Remember that this is a negative supply shock.",
            "Remember that this is a negative supply shock.",
            "This is a negative supply shock, which decreases SRAS, causing it to shift to the left in the short run while supply chains adjust.",
          ]}
        />
      </Page>
    </Navigation>
  </>
}

export default ShortRunAggregateSupply;